import { Controller } from '@hotwired/stimulus';
import { trackInteraction } from '../lib/track_interaction';

export default class extends Controller {
    static values = { presets: Array, saveUrl: String };

    static targets = [
        'progressDot',
        'choiceStep',
        'detailsStep',
        'resultStep',
        'heroTitle',
        'heroSubtitle',
        'selectedImage',
        'selectedName',
        'quantity',
        'unitLabel',
        'readyAt',
        'roomTemperature',
        'fridgeTemperature',
        'ratioButton',
        'ratioHint',
        'resultImage',
        'resultTitle',
        'resultIntro',
        'timingNotice',
        'hydrationNotice',
        'ingredients',
        'steps',
        'scheduleChoices',
        'timeline',
        'mainTip',
        'toast'
    ];

    connect() {
        this.currentPreset = null;
        this.currentRecipe = null;
        this.quantityValue = 1;
        this.starterRatio = '1:2:2';
        this.selectedScheduleIndex = 0;
        this.toastTimer = null;
        this.readyAtTarget.value = this.toInputDate(this.tomorrowAtTen());
    }

    chooseProduct(event) {
        const id = Number(event.currentTarget.dataset.presetId);
        this.currentPreset = this.presetsValue.find((preset) => Number(preset.id) === id);
        if (!this.currentPreset) return;

        this.quantityValue = 1;
        this.selectedImageTarget.src = this.currentPreset.image;
        this.selectedImageTarget.alt = this.currentPreset.name;
        this.selectedNameTarget.textContent = this.currentPreset.name;
        this.renderQuantity();
        this.showStep(2);
        trackInteraction('calculator_preset_selected', { calculator: 'simple_bread', preset_id: id });
    }

    backToChoice() {
        this.showStep(1);
    }

    backToDetails() {
        this.showStep(2);
    }

    increaseQuantity() {
        this.quantityValue = Math.min(12, this.quantityValue + 1);
        this.renderQuantity();
    }

    decreaseQuantity() {
        this.quantityValue = Math.max(1, this.quantityValue - 1);
        this.renderQuantity();
    }

    optionChanged() {}

    chooseRatio(event) {
        this.starterRatio = event.currentTarget.dataset.ratio;
        trackInteraction('calculator_ratio_selected', { calculator: 'simple_bread', ratio: this.starterRatio });
        this.ratioButtonTargets.forEach((button) =>
            button.classList.toggle('is-active', button === event.currentTarget)
        );
        this.showRatioHint(event);
    }

    showRatioHint(event) {
        this.ratioHintTarget.textContent = event.currentTarget.dataset.description;
    }

    buildRecipe() {
        if (!this.currentPreset) return;

        const config = this.productConfig(this.currentPreset.slug);
        const roomTemperature = this.clamp(this.number(this.roomTemperatureTarget.value), 18, 28);
        const fridgeTemperature = this.clamp(this.number(this.fridgeTemperatureTarget.value), 2, 10);
        const starterReserve = 20;
        const desiredOvenAt = this.parseLocalDate(this.readyAtTarget.value) || this.tomorrowAtTen();
        const recipeData = this.currentPreset.recipe_data;
        const ingredients = this.calculateIngredients(recipeData, this.quantityValue);
        const totalDough = ingredients.reduce((sum, ingredient) => sum + ingredient.grams, 0);
        const metrics = this.recipeMetrics(ingredients);
        const starterRequired = ingredients
            .filter((item) => item.role === 'starter')
            .reduce((sum, item) => sum + item.grams, 0);
        const feeding = this.convenientFeeding(starterRequired, starterReserve, this.starterRatio);
        const schedules = this.buildSchedules(
            desiredOvenAt,
            config,
            roomTemperature,
            fridgeTemperature,
            this.starterRatio,
            feeding
        );
        if (!schedules.length) {
            const fallback = this.scheduleCandidates(
                desiredOvenAt,
                desiredOvenAt,
                config,
                roomTemperature,
                fridgeTemperature,
                this.starterRatio,
                feeding
            )[0];
            schedules.push({ ...fallback, label: 'Расчёт к выбранному времени', shifted: false });
        }

        this.currentRecipe = {
            ingredients,
            totalDough: this.round(totalDough),
            estimatedBread: this.round(totalDough * 0.88),
            trueHydration: this.round(metrics.hydration),
            flour: this.round(metrics.totalFlour),
            roomTemperature,
            fridgeTemperature,
            starterRatio: this.starterRatio,
            starterReserve,
            feeding,
            desiredOvenAt,
            schedules,
            config
        };

        this.selectedScheduleIndex = 0;
        this.renderResult();
        this.showStep(3);
        trackInteraction('calculator_recipe_built', { calculator: 'simple_bread', preset_id: this.currentPreset.id });
    }

    calculateIngredients(recipeData, quantity) {
        const ingredients = recipeData.ingredients.map((item) => ({
            ...item,
            grams: this.number(item.grams) * quantity
        }));
        const metrics = this.recipeMetrics(ingredients);
        return ingredients.map((item) => ({
            ...item,
            grams: this.round(item.grams),
            percentage: this.round(metrics.totalFlour ? (item.grams / metrics.totalFlour) * 100 : 0)
        }));
    }

    recipeMetrics(ingredients) {
        let totalFlour = 0;
        let hydrationWater = 0;
        ingredients.forEach((item) => {
            const grams = this.number(item.grams);
            if (item.role === 'flour') totalFlour += grams;
            if (item.role === 'water') hydrationWater += grams;
            if (item.role === 'liquid') hydrationWater += grams * this.number(item.water_fraction);
            if (item.role === 'starter') {
                const starterFlour = grams / 2;
                totalFlour += starterFlour;
                hydrationWater += grams - starterFlour;
            }
        });
        return { totalFlour, hydrationWater, hydration: totalFlour > 0 ? (hydrationWater / totalFlour) * 100 : 0 };
    }

    convenientFeeding(required, reserve, ratio) {
        const flourParts = Number(ratio.split(':')[1]);
        const totalParts = 1 + flourParts * 2;
        const onePart = Math.max(5, Math.ceil((required + reserve) / totalParts / 5) * 5);
        const flour = onePart * flourParts;
        const water = onePart * flourParts;
        const total = onePart + flour + water;
        return { seed: onePart, flour, water, total, recipe: required, reserve: total - required };
    }

    buildSchedules(desiredOvenAt, config, roomTemperature, fridgeTemperature, ratio, feeding) {
        const exact = this.scheduleCandidates(
            desiredOvenAt,
            desiredOvenAt,
            config,
            roomTemperature,
            fridgeTemperature,
            ratio,
            feeding
        ).find((schedule) => this.scheduleIsComfortable(schedule));
        if (exact) return [{ ...exact, label: 'К выбранному времени', shifted: false }];

        const bySession = new Map();
        const fallbacks = [];
        for (let offset = 0; offset <= 72 * 60; offset += 30) {
            const ovenAt = this.addMinutes(desiredOvenAt, offset);
            for (const schedule of this.scheduleCandidates(
                ovenAt,
                desiredOvenAt,
                config,
                roomTemperature,
                fridgeTemperature,
                ratio,
                feeding
            )) {
                if (!this.scheduleIsComfortable(schedule)) continue;
                const session = this.scheduleSession(schedule);
                const candidate = { ...schedule, label: session, shifted: offset > 0 };
                if (!bySession.has(session)) bySession.set(session, candidate);
                if (
                    fallbacks.length < 2 &&
                    !fallbacks.some((item) => item.ovenAt.getTime() === candidate.ovenAt.getTime())
                )
                    fallbacks.push(candidate);
            }
            if (bySession.size >= 2) break;
        }

        const choices = [...bySession.values()].sort((a, b) => a.ovenAt - b.ovenAt);
        for (const fallback of fallbacks) {
            if (choices.length >= 2) break;
            const alreadyIncluded = choices.some(
                (item) =>
                    item.ovenAt.getTime() === fallback.ovenAt.getTime() && item.coldMinutes === fallback.coldMinutes
            );
            if (!alreadyIncluded) choices.push({ ...fallback, label: 'Другой удобный вариант' });
        }
        return choices.slice(0, 2);
    }

    scheduleCandidates(ovenAt, desiredOvenAt, config, roomTemperature, fridgeTemperature, ratio, feeding) {
        const fridgeFactor = this.clamp(1 - (fridgeTemperature - 5) * 0.03, 0.85, 1.15);
        const coldMin = Math.round((config.coldRange[0] * 60 * fridgeFactor) / 30) * 30;
        const coldMax = Math.round((config.coldRange[1] * 60 * fridgeFactor) / 30) * 30;
        const midpoint = (coldMin + coldMax) / 2;
        const candidates = [];
        for (let cold = coldMin; cold <= coldMax; cold += 30) {
            candidates.push(
                this.buildSchedule(
                    ovenAt,
                    desiredOvenAt,
                    config,
                    roomTemperature,
                    fridgeTemperature,
                    ratio,
                    feeding,
                    cold
                )
            );
        }
        return candidates.sort((a, b) => Math.abs(a.coldMinutes - midpoint) - Math.abs(b.coldMinutes - midpoint));
    }

    buildSchedule(ovenAt, desiredOvenAt, config, roomTemperature, fridgeTemperature, ratio, feeding, coldMinutes) {
        const roomFactor = this.clamp(2 ** ((24 - roomTemperature) / 8), 0.65, 1.75);
        const bulkRange = config.bulkRange.map((minutes) => Math.round((minutes * roomFactor) / 15) * 15);
        const bulkMinutes = Math.round((bulkRange[0] + bulkRange[1]) / 2 / 15) * 15;
        const starterBase = this.starterPeakWindow(ratio);
        const starterRange = starterBase.map((minutes) => Math.round((minutes * roomFactor) / 15) * 15);
        const starterMinutes = Math.round((starterRange[0] + starterRange[1]) / 2 / 15) * 15;

        const coldEnd = this.addMinutes(ovenAt, -config.postCold);
        const shapeAt = this.addMinutes(coldEnd, -coldMinutes);
        const preShapeAt = this.addMinutes(shapeAt, -config.benchRest);
        const bulkAt = this.addMinutes(preShapeAt, -bulkMinutes);
        const mixAt = this.addMinutes(bulkAt, -config.mix);
        const autolyseAt = this.addMinutes(mixAt, -config.autolyse);
        const feedAt = this.addMinutes(autolyseAt, -starterMinutes);
        const peakStart = this.addMinutes(feedAt, starterRange[0]);
        const peakEnd = this.addMinutes(feedAt, starterRange[1]);
        const preheatAt = this.addMinutes(ovenAt, -config.preheat);
        const bakeEnd = this.addMinutes(ovenAt, config.bakeMinutes);
        const coolEnd = this.addMinutes(bakeEnd, config.cool);

        const items = [
            this.timelineItem(
                'Кормление закваски',
                feedAt,
                `Смешайте ${this.formatNumber(feeding.seed)} г материнской закваски, ${this.formatNumber(feeding.flour)} г муки и ${this.formatNumber(feeding.water)} г воды по схеме ${ratio}.`
            ),
            this.timelineItem(
                'Созревание закваски',
                peakStart,
                'Используйте на пике: выраженный купол, много пузырьков, объём вырос минимум вдвое и ещё не начал опадать.',
                peakEnd
            ),
            this.timelineItem(
                'Автолиз',
                autolyseAt,
                `${config.autolyseHint} Оставьте примерно на ${config.autolyse} минут.`
            ),
            this.timelineItem('Соль, дополнения и основной замес', mixAt, config.mixHint),
            this.timelineItem(
                'Комнатное брожение',
                bulkAt,
                `${config.bulkHint} Ориентир — ${config.volumeTarget}.`,
                preShapeAt
            ),
            ...(config.benchRest > 0
                ? [
                      this.timelineItem(
                          'Предформовка',
                          preShapeAt,
                          `Соберите заготовку и дайте тесту расслабиться ${config.benchRest} минут.`
                      )
                  ]
                : []),
            this.timelineItem('Формовка', shapeAt, config.shapeHint),
            this.timelineItem(
                'Холодная ферментация',
                shapeAt,
                `Накройте тесто и оставьте при ${this.formatNumber(fridgeTemperature)} °C. При более тёплом холодильнике проверяйте тесто раньше.`,
                coldEnd
            ),
            this.timelineItem('Разогрев духовки', preheatAt, config.preheatHint),
            this.timelineItem('Выпечка', ovenAt, config.bakeHint, bakeEnd),
            this.timelineItem('Остывание', bakeEnd, config.coolHint, coolEnd)
        ];
        if (config.postCold > 0)
            items.push(this.timelineItem(config.postColdTitle, coldEnd, config.postColdHint, ovenAt));
        items.sort((a, b) => a.time - b.time);

        const activeWindows = [
            [feedAt, 10],
            [autolyseAt, 10],
            [mixAt, config.mix],
            ...(config.benchRest > 0 ? [[preShapeAt, 10]] : []),
            [shapeAt, 15],
            [preheatAt, 10],
            [ovenAt, 10]
        ];
        if (config.postCold > 0) activeWindows.push([coldEnd, Math.min(config.postCold, 30)]);

        return {
            desiredOvenAt,
            ovenAt,
            coldMinutes,
            autolyseAt,
            firstAction: feedAt,
            activeWindows,
            items
        };
    }

    starterPeakWindow(ratio) {
        return (
            {
                '1:1:1': [240, 360],
                '1:2:2': [285, 405],
                '1:3:3': [330, 465],
                '1:4:4': [375, 525],
                '1:5:5': [420, 600]
            }[ratio] || [285, 405]
        );
    }

    scheduleIsComfortable(schedule) {
        const notPast = schedule.firstAction >= this.addMinutes(new Date(), -15);
        return notPast && schedule.activeWindows.every(([time, duration]) => this.isDaytimeWindow(time, duration));
    }

    isDaytimeWindow(time, duration) {
        const start = time.getHours() * 60 + time.getMinutes();
        return start >= 7 * 60 && start + duration <= 23 * 60;
    }

    scheduleSession(schedule) {
        const hour = schedule.autolyseAt.getHours();
        if (hour < 12) return 'Утренний замес';
        if (hour < 17) return 'Дневной замес';
        return 'Вечерний замес';
    }

    renderResult() {
        const recipe = this.currentRecipe;
        const schedule = recipe.schedules[this.selectedScheduleIndex];
        const quantityText = `${this.quantityValue} ${this.pluralUnit(this.currentPreset.slug, this.quantityValue)}`;
        this.resultImageTarget.src = this.currentPreset.image;
        this.resultImageTarget.alt = this.currentPreset.name;
        this.resultTitleTarget.textContent = this.currentPreset.name;
        this.resultIntroTarget.textContent = `Рецепт рассчитан на ${quantityText}. План построен к постановке в духовку ${this.formatDateTime(recipe.desiredOvenAt)}.`;
        if (this.currentPreset.slug === 'toast')
            this.resultIntroTarget.textContent += ' На каждую буханку нужна форма 21 × 11,5 × 11 см с крышкой.';

        const shifted = schedule.ovenAt.getTime() !== recipe.desiredOvenAt.getTime();
        this.timingNoticeTarget.classList.toggle('hidden', !shifted);
        if (shifted) {
            this.timingNoticeTarget.textContent = `К выбранному времени активные этапы попали бы на ночь. Предлагаем удобный график с постановкой в духовку ${this.formatDateTime(schedule.ovenAt)}.`;
        }

        const waterOption = this.currentPreset.recipe_data.water_option;
        if (waterOption) {
            const baseWater = recipe.ingredients.find((ingredient) => ingredient.key === waterOption.key)?.grams || 0;
            const maximumWater = this.number(waterOption.maximum) * this.quantityValue;
            this.hydrationNoticeTarget.classList.remove('hidden');
            this.hydrationNoticeTarget.textContent = `Базовое количество воды — ${this.formatNumber(baseWater)} г. Если мука хорошо впитывает воду, добавляйте её постепенно, максимум до ${this.formatNumber(maximumWater)} г. Это рекомендация, а не обязательное увеличение.`;
        } else {
            this.hydrationNoticeTarget.classList.toggle('hidden', recipe.trueHydration < 72);
            this.hydrationNoticeTarget.textContent = `Гидратация теста — ${this.formatNumber(recipe.trueHydration)}%. Для такого теста нужна сильная, влагоёмкая мука.`;
        }

        this.ingredientsTarget.innerHTML = recipe.ingredients
            .map(
                (ingredient) => `
      <div class="simple-ingredient">
        <span class="text-sm text-stone-600">${this.escape(ingredient.name)}</span>
        <strong class="text-sm font-semibold text-stone-900">${this.formatNumber(ingredient.grams)} г</strong>
      </div>
    `
            )
            .join('');

        this.stepsTarget.innerHTML = this.recipeSteps(recipe.config, recipe)
            .map(
                (step, index) => `
      <div class="simple-step">
        <span class="simple-step-number">${index + 1}</span>
        <div>
          <div class="text-sm font-semibold text-stone-800">${this.escape(step.title)}</div>
          <p class="mt-1 text-xs leading-5 text-stone-500">${this.escape(step.text)}</p>
        </div>
      </div>
    `
            )
            .join('');

        this.renderScheduleChoices();
        this.renderTimeline(schedule);

        this.mainTipTarget.textContent = this.mainTip(recipe.config);
    }

    renderScheduleChoices() {
        const schedules = this.currentRecipe.schedules;
        this.scheduleChoicesTarget.classList.toggle('hidden', schedules.length < 2);
        this.scheduleChoicesTarget.classList.toggle('grid', schedules.length >= 2);
        this.scheduleChoicesTarget.innerHTML = schedules
            .map(
                (schedule, index) => `
        <button type="button" class="simple-schedule-choice ${index === this.selectedScheduleIndex ? 'is-active' : ''}" data-index="${index}" data-action="simple-bread-calculator#selectSchedule">
          <strong>${this.escape(schedule.label)}</strong>
          Замес: ${this.formatDateTime(schedule.autolyseAt)}<br>Духовка: ${this.formatDateTime(schedule.ovenAt)}
        </button>
      `
            )
            .join('');
    }

    selectSchedule(event) {
        this.selectedScheduleIndex = Number(event.currentTarget.dataset.index);
        this.renderResult();
        trackInteraction('calculator_schedule_selected', {
            calculator: 'simple_bread',
            schedule_index: this.selectedScheduleIndex
        });
    }

    renderTimeline(schedule) {
        let previousDay = '';
        this.timelineTarget.innerHTML = schedule.items
            .map((item) => {
                const dayKey = this.dateKey(item.time);
                const dayHeading =
                    dayKey === previousDay
                        ? ''
                        : `<div class="simple-timeline-day">${this.escape(this.dayLabel(item.time))}</div>`;
                previousDay = dayKey;
                return `${dayHeading}
        <div class="simple-timeline-item">
          <time class="pt-0.5 text-[11px] font-semibold leading-4 text-[#8d6543]">${this.formatScheduleWindow(item.time, item.endTime)}</time>
          <span class="relative z-10 mt-1 size-2.5 justify-self-center rounded-full border-2 border-white bg-[#bd9470] ring-1 ring-[#d7bda4]"></span>
          <div>
            <div class="text-xs font-semibold text-stone-800">${this.escape(item.label)}</div>
            <p class="mt-1 text-[11px] leading-4 text-stone-500">${this.escape(item.description)}</p>
          </div>
        </div>`;
            })
            .join('');
    }

    recipeSteps(config, recipe) {
        const feeding = recipe.feeding;
        const openingSteps = [
            {
                title: 'Покормите закваску',
                text: `${this.formatNumber(feeding.seed)} г материнской закваски + ${this.formatNumber(feeding.flour)} г муки + ${this.formatNumber(feeding.water)} г воды. После созревания возьмите ${this.formatNumber(feeding.recipe)} г в тесто, останется ${this.formatNumber(feeding.reserve)} г.`
            },
            {
                title: 'Дождитесь пика',
                text: 'Закваска выросла минимум вдвое, покрыта пузырьками, имеет выраженный купол и ещё не начала опадать.'
            },
            { title: 'Проведите автолиз', text: config.autolyseHint },
            { title: 'Введите соль и дополнения', text: config.mixHint }
        ];
        const bulkStep = {
            title: 'Комнатное брожение',
            text: `${config.bulkHint} Переходите дальше, когда ${config.volumeTarget}.`
        };

        if (config.compactToastSteps) {
            return [
                ...openingSteps,
                bulkStep,
                { title: 'Формовка', text: `${config.shapeHint} ${config.coldHint}` },
                {
                    title: 'Выпечка',
                    text: `${config.postColdHint} ${config.preheatHint} ${config.bakeHint} ${config.coolHint}`
                }
            ];
        }

        return [
            ...openingSteps,
            bulkStep,
            ...(config.benchRest > 0
                ? [
                      {
                          title: 'Предформовка',
                          text: `Соберите заготовку и оставьте отдохнуть примерно на ${config.benchRest} минут.`
                      }
                  ]
                : []),
            { title: 'Формовка', text: config.shapeHint },
            { title: 'Холодная ферментация', text: config.coldHint },
            { title: 'Выпечка', text: config.bakeHint },
            { title: 'Остывание', text: config.coolHint }
        ];
    }

    productConfig(slug) {
        const podBread = {
            autolyse: 30,
            mix: 25,
            benchRest: 20,
            coldRange: [12, 14],
            postCold: 0,
            postColdTitle: 'Подготовка к выпечке',
            preheat: 60,
            bakeMinutes: 45,
            cool: 120,
            autolyseHint: 'Смешайте муку, воду и зрелую закваску. Оставьте тесто отдохнуть.',
            bulkHint:
                'В первые два часа укрепляйте клейковину складываниями или ламинированием. Для высокой гидратации ориентир — четыре укрепления примерно через 30 минут.',
            volumeTarget: 'тесто увеличилось примерно на 50%, стало воздушным, а по краям появились пузырьки',
            coldHint: 'Оставьте сформованную заготовку в холодильнике ориентировочно на 12–14 часов.',
            preheatHint: 'Разогревайте духовку вместе с казаном около часа до 250 °C.',
            bakeHint:
                'Посадите хлеб в горячий казан. Пеките 15 минут под крышкой при 250 °C, затем снимите крышку, снизьте температуру до 220–230 °C и допекайте ещё 20–30 минут до выраженного хлебного колера.',
            coolHint: 'Полностью остудите хлеб на решётке, обычно это занимает около 2 часов.'
        };
        const configs = {
            toast: {
                units: ['хлеб', 'хлеба', 'хлебов'],
                compactToastSteps: true,
                autolyse: 30,
                mix: 30,
                benchRest: 0,
                bulkRange: [180, 240],
                coldRange: [12, 14],
                postCold: 60,
                postColdTitle: 'Проверка расстойки и разогрев',
                preheat: 45,
                bakeMinutes: 55,
                cool: 90,
                autolyseHint: 'Смешайте пшеничную муку, воду, молоко и зрелую закваску. Оставьте смесь отдохнуть.',
                mixHint:
                    'Добавьте соль и сахар, затем постепенно введите мягкое сливочное масло и развивайте клейковину до гладкости теста.',
                bulkHint: 'За время комнатного брожения сделайте 2 складывания или 1 ламинирование.',
                volumeTarget: 'тесто увеличилось примерно на 30%',
                shapeHint: 'Сформуйте тостовый хлеб и переложите в форму, предварительно смазанную сливочным маслом.',
                coldHint: 'Накройте форму и поставьте тесто в холодильник на 12–14 часов.',
                postColdHint:
                    'На следующий день достаньте форму с тостовым хлебом. Тесто должно подняться так, чтобы до крышки оставалось 1,5–2 см. Если в холодильнике тесто не достигло этого уровня, оставьте его при комнатной температуре до нужного подъёма и только после этого выпекайте.',
                preheatHint: 'Разогрейте духовку до 230 °C.',
                bakeHint:
                    'Выпекайте первые 20 минут при 230 °C, затем снизьте температуру до 190 °C и выпекайте ещё 30–35 минут.',
                coolHint: 'Сразу достаньте хлеб из формы и полностью остудите на решётке, не менее 1–1,5 часа.'
            },
            french: {
                ...podBread,
                units: ['булка', 'булки', 'булок'],
                bulkRange: [210, 270],
                mixHint:
                    'Добавьте соль и сахар, выполните основной замес, затем постепенно введите мягкое сливочное масло.',
                shapeHint: 'Соберите тесто в аккуратную заготовку, сохраняя накопившийся внутри воздух.'
            },
            malt: {
                ...podBread,
                units: ['хлеб', 'хлеба', 'хлебов'],
                bulkRange: [240, 300],
                autolyseHint: 'Смешайте муку, воду и зрелую закваску. Заварку заранее полностью остудите.',
                mixHint:
                    'Добавьте соль, остывшую заварку из ржаного ферментированного солода и мёд, затем выполните основной замес. По желанию внесите 30 г сушёной клюквы во время складывания или ламинирования: заранее залейте её горячей водой на 30 минут, затем хорошо слейте воду и обсушите бумажным полотенцем.',
                shapeHint: 'Сформуйте плотную круглую заготовку и положите в корзину швом вверх.'
            },
            tartine: {
                ...podBread,
                units: ['хлеб', 'хлеба', 'хлебов'],
                bulkRange: [240, 300],
                mixHint: 'Добавьте соль и оставшуюся по рецепту воду, затем развивайте тесто бережным замесом.',
                shapeHint: 'Подтяните поверхность заготовки, сохраняя внутри накопившийся воздух.'
            },
            ciabatta: {
                units: ['чиабатта', 'чиабатты', 'чиабатт'],
                autolyse: 30,
                mix: 25,
                benchRest: 15,
                bulkRange: [210, 270],
                coldRange: [12, 16],
                postCold: 30,
                postColdTitle: 'Деление и формовка',
                preheat: 45,
                bakeMinutes: 35,
                cool: 60,
                autolyseHint: 'Смешайте муку, воду и зрелую закваску. Оставьте влажное тесто отдохнуть.',
                mixHint: 'Добавьте соль, выполните основной замес и постепенно введите оливковое масло.',
                bulkHint:
                    'Укрепляйте клейковину складываниями или ламинированием. В первые два часа для высокой гидратации полезны четыре укрепления примерно через 30 минут.',
                volumeTarget: 'тесто увеличилось минимум на 50%, стало дрожащим и наполненным пузырьками',
                shapeHint:
                    'Выложите тесто на муку, разделите и лишь слегка подтяните форму. Не прижимайте и не выпускайте пузырьки.',
                coldHint: 'Оставьте тесто на ночную холодную ферментацию ориентировочно на 12–16 часов.',
                postColdHint:
                    'Дайте тесту 10–15 минут при комнатной температуре, затем бережно разделите и сформуйте чиабатты.',
                preheatHint: 'Разогрейте духовку до 235–240 °C. Подготовьте привычную подачу пара.',
                bakeHint:
                    'Первые 15 минут выпекайте при 235–240 °C с паром, затем выпустите пар и продолжайте ещё 15–20 минут до насыщенного золотистого цвета.',
                coolHint: 'Полностью остудите чиабатту на решётке около 45–60 минут.'
            },
            pizza: {
                units: ['пицца', 'пиццы', 'пицц'],
                autolyse: 30,
                mix: 25,
                benchRest: 15,
                bulkRange: [90, 90],
                coldRange: [24, 48],
                postCold: 120,
                postColdTitle: 'Деление, согревание и подготовка',
                preheat: 60,
                bakeMinutes: 12,
                cool: 5,
                autolyseHint: 'Смешайте муку, воду и зрелую закваску. Оставьте тесто отдохнуть.',
                mixHint:
                    'Добавьте соль и остальные ингредиенты по рецепту, затем развивайте клейковину до гладкости теста.',
                bulkHint:
                    'Оставьте тесто на 30 минут, укрепите клейковину складыванием или ламинированием и оставьте ещё примерно на час.',
                volumeTarget: 'прошёл предусмотренный комнатный этап; увеличения на 50% ждать не нужно',
                shapeHint: 'Соберите тесто для холодильника, не затягивая поверхность чрезмерно.',
                coldHint: 'Основная ферментация проходит в холодильнике 24–48 часов.',
                postColdHint:
                    'Достаньте тесто, разделите на порции, округлите и дайте согреться. Затем растяните руками и добавьте начинку.',
                preheatHint:
                    'Разогревайте духовку около часа на максимальной доступной температуре в режиме верх-низ без конвекции.',
                bakeHint:
                    'При посадке подайте пар. Выпекайте при максимально доступной температуре: ориентировочно 250–300 °C в течение 7–12 минут, до румяного борта и активно кипящего сыра.',
                coolHint: 'Переложите пиццу на решётку на несколько минут, затем подавайте.'
            },
            fitness: {
                ...podBread,
                units: ['хлеб', 'хлеба', 'хлебов'],
                bulkRange: [240, 300],
                seeds: true,
                autolyseHint:
                    'Смешайте всю муку, воду основного теста и зрелую закваску. Лён заранее залейте кипятком на 20–30 минут.',
                mixHint:
                    'Добавьте соль и развивайте клейковину. Набухший лён вместе с гелем внесите позже во время складывания или ламинирования.',
                shapeHint: 'Сформуйте ровный овал и хорошо запечатайте шов.'
            },
            pumpkin: {
                ...podBread,
                units: ['хлеб', 'хлеба', 'хлебов'],
                bulkRange: [240, 300],
                autolyseHint:
                    'Смешайте пшеничную муку, базовое количество воды, тыквенное пюре и зрелую закваску. Оставьте тесто отдохнуть.',
                mixHint:
                    'Добавьте соль и мёд, затем постепенно введите мягкое сливочное масло. Подсушенные и полностью остывшие семена тыквы внесите после развития клейковины, во время складывания или ламинирования.',
                shapeHint: 'Сформуйте ровную круглую или овальную заготовку и положите в корзину швом вверх.'
            }
        };
        return configs[slug] || configs.tartine;
    }

    mainTip(config) {
        if (this.currentPreset.slug === 'ciabatta')
            return 'Не пытайтесь сделать влажное тесто «удобным» с помощью лишней муки. Вода на руках и бережные складывания работают лучше.';
        if (this.currentPreset.slug === 'pizza')
            return 'Самый заметный вкус даёт не дополнительная закваска, а спокойное созревание теста в холодильнике.';
        if (this.currentPreset.slug === 'malt')
            return 'По желанию добавьте 30 г сушёной клюквы. Залейте её горячей водой на 30 минут, затем хорошо слейте воду и обсушите бумажным полотенцем.';
        if (this.currentPreset.slug === 'toast')
            return 'Перед формовкой ориентируйтесь на увеличение теста примерно на 30%, а после холодильника — на расстояние 1,5–2 см до крышки формы.';
        if (this.currentPreset.slug === 'pumpkin')
            return 'Подсушите тыквенные семена на сухой сковороде и полностью остудите перед внесением: так они сохранят аромат и не нагреют тесто.';
        if (config.seeds)
            return 'Семена обязательно замочите заранее: иначе они заберут влагу из мякиша, и хлеб получится сухим.';
        return 'Смотрите на тесто, а не только на часы: оно готово к следующему шагу, когда стало воздушным и увеличилось примерно наполовину.';
    }

    showStep(number) {
        this.choiceStepTarget.classList.toggle('hidden', number !== 1);
        this.detailsStepTarget.classList.toggle('hidden', number !== 2);
        this.resultStepTarget.classList.toggle('hidden', number !== 3);
        this.progressDotTargets.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === number - 1);
            dot.classList.toggle('is-complete', index < number - 1);
        });
        const headings = {
            1: [
                'Что сегодня испечём?',
                'Ответьте на несколько простых вопросов. Все расчёты и пекарские тонкости мы возьмём на себя.'
            ],
            2: ['Пара простых вопросов', 'Укажите количество, температуру и время постановки в духовку.'],
            3: ['Ваш рецепт готов', 'Мы рассчитали ингредиенты, объяснили каждый шаг и составили удобное расписание.']
        };
        this.heroTitleTarget.textContent = headings[number][0];
        this.heroSubtitleTarget.textContent = headings[number][1];
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    renderQuantity() {
        this.quantityTarget.textContent = this.quantityValue;
        this.unitLabelTarget.textContent = this.pluralUnit(this.currentPreset.slug, this.quantityValue);
    }

    pluralUnit(slug, count) {
        const forms = this.productConfig(slug).units;
        const mod100 = count % 100;
        const mod10 = count % 10;
        if (mod100 >= 11 && mod100 <= 19) return forms[2];
        if (mod10 === 1) return forms[0];
        if (mod10 >= 2 && mod10 <= 4) return forms[1];
        return forms[2];
    }

    timelineItem(label, time, description, endTime = null) {
        return { label, time, description, endTime };
    }

    async copy() {
        if (!this.currentRecipe) return;
        try {
            await navigator.clipboard.writeText(this.recipeText());
            this.showToast('Рецепт скопирован');
            trackInteraction('calculator_result_copied', { calculator: 'simple_bread' });
        } catch (_) {
            this.showToast('Не удалось скопировать рецепт', true);
        }
    }

    async save() {
        if (!this.currentRecipe) return;
        const schedule = this.currentRecipe.schedules[this.selectedScheduleIndex];
        const defaultName = `${this.currentPreset.name} — ${this.formatDate(schedule.ovenAt)}`;
        const name = window.prompt('Название рецепта', defaultName);
        if (!name?.trim()) return;

        const recipe = this.currentRecipe;
        const body = {
            saved_bread_recipe: {
                bread_preset_id: this.currentPreset.id,
                name: name.trim(),
                calculation_basis: 'flour',
                target_weight: recipe.flour,
                parameters: {
                    quantity: this.quantityValue,
                    unit: this.pluralUnit(this.currentPreset.slug, this.quantityValue),
                    room_temperature: recipe.roomTemperature,
                    fridge_temperature: recipe.fridgeTemperature,
                    starter_ratio: recipe.starterRatio,
                    starter_reserve: recipe.starterReserve,
                    desired_oven_at: recipe.desiredOvenAt.toISOString(),
                    planned_oven_at: schedule.ovenAt.toISOString(),
                    hydration: recipe.trueHydration
                },
                result: {
                    total_dough: recipe.totalDough,
                    estimated_bread: recipe.estimatedBread,
                    true_hydration: recipe.trueHydration,
                    flour: recipe.flour,
                    ingredients: recipe.ingredients,
                    feeding: recipe.feeding,
                    timeline: schedule.items.map((item) => ({
                        label: item.label,
                        time: item.time.toISOString(),
                        end_time: item.endTime?.toISOString(),
                        description: item.description
                    }))
                }
            }
        };

        try {
            const response = await fetch(this.saveUrlValue, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Не удалось сохранить рецепт');
            this.showToast(data.message || 'Рецепт сохранён');
        } catch (error) {
            this.showToast(error.message, true);
        }
    }

    print() {
        trackInteraction('calculator_result_printed', { calculator: 'simple_bread' });
        const oldTitle = document.title;
        document.title = `${this.currentPreset.name} — мой рецепт`;
        window.print();
        document.title = oldTitle;
    }

    recipeText() {
        const recipe = this.currentRecipe;
        const schedule = recipe.schedules[this.selectedScheduleIndex];
        const ingredients = recipe.ingredients.map((item) => `${item.name}: ${this.formatNumber(item.grams)} г`);
        const feeding = `${this.formatNumber(recipe.feeding.seed)} г закваски + ${this.formatNumber(recipe.feeding.flour)} г муки + ${this.formatNumber(recipe.feeding.water)} г воды`;
        const scheduleLines = schedule.items.map(
            (item) => `${this.formatScheduleWindow(item.time, item.endTime)} — ${item.label}: ${item.description}`
        );
        return [
            this.currentPreset.name,
            '',
            'Ингредиенты',
            ...ingredients,
            '',
            `Кормление ${recipe.starterRatio}`,
            feeding,
            '',
            'Расписание',
            ...scheduleLines,
            '',
            'Время является ориентиром — ориентируйтесь на состояние закваски и теста.'
        ].join('\n');
    }

    showToast(message, error = false) {
        clearTimeout(this.toastTimer);
        this.toastTarget.textContent = message;
        this.toastTarget.classList.toggle('bg-[#945b50]', error);
        this.toastTarget.classList.toggle('bg-[#4f3a2b]', !error);
        this.toastTarget.classList.remove('opacity-0', 'translate-y-3');
        this.toastTimer = setTimeout(() => this.toastTarget.classList.add('opacity-0', 'translate-y-3'), 2800);
    }

    tomorrowAtTen() {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        date.setHours(10, 0, 0, 0);
        return date;
    }

    parseLocalDate(value) {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    toInputDate(date) {
        const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
        return local.toISOString().slice(0, 16);
    }

    addMinutes(date, minutes) {
        return new Date(date.getTime() + minutes * 60_000);
    }

    roundToNextQuarter(date) {
        const rounded = new Date(date);
        rounded.setSeconds(0, 0);
        rounded.setMinutes(Math.ceil(rounded.getMinutes() / 15) * 15);
        return rounded;
    }

    formatTime(date) {
        return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date);
    }

    formatScheduleTime(date) {
        return new Intl.DateTimeFormat('ru-RU', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    }

    formatScheduleWindow(start, end) {
        if (!end) return this.formatTime(start);
        const sameDay = this.dateKey(start) === this.dateKey(end);
        return sameDay
            ? `${this.formatTime(start)}–${this.formatTime(end)}`
            : `${this.formatScheduleTime(start)}–${this.formatScheduleTime(end)}`;
    }

    dateKey(date) {
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    }

    dayLabel(date) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        if (this.dateKey(date) === this.dateKey(today)) return 'Сегодня';
        if (this.dateKey(date) === this.dateKey(tomorrow)) return 'Завтра';
        return new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
    }

    formatDate(date) {
        return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    }

    formatDateTime(date) {
        return new Intl.DateTimeFormat('ru-RU', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    }

    formatNumber(value) {
        return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);
    }

    number(value) {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
    }

    round(value) {
        return Math.round((value + Number.EPSILON) * 10) / 10;
    }

    escape(value) {
        const element = document.createElement('div');
        element.textContent = String(value ?? '');
        return element.innerHTML.replaceAll('"', '&quot;');
    }
}
